import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useData } from '../../../contexts/DataContext';
import { usePreferences } from '../../../contexts/PreferencesContext';
import { useToast } from '../../../contexts/ToastContext';
import { normalizeSyllabusResponse } from '../../../services/ai/syllabusNormalizer';
import { EndeavorApi } from '../../../services/api/EndeavorApi';
import { EndeavorTypeApi } from '../../../services/api/EndeavorTypeApi';
import { EventTypeApi } from '../../../services/api/EventTypeApi';
import { DocumentEndeavorApi } from '../../../services/api/DocumentEndeavorAPI';
// EndeavorMetadataApi may be used later for saving course metadata
// import { EndeavorMetadataApi } from '../../../services/api/EndeavorMetadataApi';
import { UsageEventsTracker } from '../../../services/UsageEventsTracker';
import CourseMatchBanner from '../../../components/onboarding/CourseMatchBanner';

// ── Helpers (mirrored from CreateCourseWizard) ─────────────────────
const sanitizeDateTime = (val) => {
  if (!val || typeof val !== 'string') return val;
  let s = val.trim();
  if ((s.match(/T/g) || []).length > 1) {
    const parts = s.split('T');
    if (parts.length >= 3) {
      const second = parts[1];
      if (/[+-]\d{2}:?\d{2}|Z/.test(second)) { s = parts[0] + 'T' + second; }
      else { s = parts.slice(0, 2).join('T'); }
    }
    s = s.replace(/T{2,}/g, 'T');
  }
  s = s.replace(/[^0-9TZ:+-].*$/, '');
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s + 'T00:00:00';
  // Pad single-digit hours: T7:00 → T07:00
  s = s.replace(/T(\d):/,  'T0$1:');
  return s;
};

const toNaiveDateTime = (val) => {
  if (!val) return val;
  let s = String(val).trim();
  s = sanitizeDateTime(s) || s;
  s = s.replace(/([+-]\d{2}:?\d{2}|Z)$/, '');
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s + 'T23:59:59';
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(s)) return s + ':00';
  return s;
};

const ensureId = (item) => {
  if (!item || typeof item !== 'object') return item;
  if (item.id) return item;
  const gen = (typeof window !== 'undefined' && window.crypto?.randomUUID) ? window.crypto.randomUUID() : null;
  const id = gen || `tmp_${Date.now()}_${Math.floor(Math.random() * 100000)}`;
  return { ...item, id };
};

const toYMD = (d) => {
  if (!d) return '';
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  } catch (_e) { return ''; }
};

const normalizeEvents = (events = []) => (events || []).map(e => {
  const ensured = ensureId(e);
  const sourceId = e.eventId || e.id || e.event_id || null;
  return { ...ensured, sourceId };
});

const normalizeAssignments = (assignments = []) => (assignments || []).map(a => {
  const ensured = ensureId(a);
  const src = a.dueDate || a.dueDatetime || a.date || a.due_at || a.start || a.assignedDate || null;
  const dueDate = toYMD(src);
  const sourceId = a.assignmentId || a.id || a.assignment_id || null;
  return { ...ensured, sourceId, dueDate };
});

/**
 * Step 5 — Course Review & Creation
 * Wraps the existing CourseReviewSection with onboarding-level course creation logic.
 * Supports multi-course mode: receives courseIndex + totalCourses to loop.
 */
export default function CourseReviewStep({ data, updateData, goNext, goBack, courseIndex = 0, totalCourses = 1 }) {
  const { user } = useAuth();
  const { patchPreferences } = usePreferences();
  const { hydrateDashboard, invalidateCache, clearBulkCache, setCreateCourseDraft, createCourseDraft, updateDraftEvent, deleteDraftEvent, addDraftEvent, updateDraftAssignment, deleteDraftAssignment, addDraftAssignment } = useData();
  const { addToast } = useToast();
  const [courseData, setCourseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const initializedRef = useRef(false);

  // ── Inline editing state ──
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editingAssignmentId, setEditingAssignmentId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [addingAssignmentForSession, setAddingAssignmentForSession] = useState(null);
  const [newAssignmentForm, setNewAssignmentForm] = useState({ title: '', dueDate: '', dueTime: '23:59', assignmentType: 'homework' });
  const [justAddedId, setJustAddedId] = useState(null);
  const addPanelRef = useRef(null);
  const [legendHover, setLegendHover] = useState(null);
  const [hoveredStripDay, setHoveredStripDay] = useState(null);

  // ── Shared course toggle ──
  const [makeShared, setMakeShared] = useState(false);

  // ── Inline course name editing ──
  const [editingCourseName, setEditingCourseName] = useState(false);
  const courseNameInputRef = useRef(null);

  // ── Add Event state ──
  const [addingEventMode, setAddingEventMode] = useState(null); // null | 'global' | sessionId
  const [newEventForm, setNewEventForm] = useState({ title: '', date: '', startTime: '', endTime: '', location: '', eventType: 'class' });
  const [justAddedEventId, setJustAddedEventId] = useState(null);

  // ── Quick schedule generator state ──
  const [schedDays, setSchedDays] = useState([]);
  const [schedStart, setSchedStart] = useState('09:00');
  const [schedEnd, setSchedEnd] = useState('10:15');
  const [schedLocation, setSchedLocation] = useState('');
  const [showScheduleGen, setShowScheduleGen] = useState(false);

  // ── Skip course handler ──
  const skipCourse = useCallback(() => {
    addToast({ message: 'Course skipped — you can add it anytime', intent: 'neutral', duration: 4000 });
    goNext();
  }, [addToast, goNext]);

  // Pick the right scan result for this course iteration.
  // Uses scanIds for positional mapping — scanIds[i] corresponds to course i.
  const scanResultForCourse = React.useMemo(() => {
    // Course 0 — prefer the eagerly-set primary scanResult
    if (courseIndex === 0 && data.scanResult) return data.scanResult;

    const scanIds = data.scanIds || [];
    const targetScanId = scanIds[courseIndex];
    if (!targetScanId) return null;

    // Search background scan results by matching _scanId
    if (Array.isArray(data.scanResults)) {
      const match = data.scanResults.find(
        sr => sr?._scanId != null && String(sr._scanId) === String(targetScanId)
      );
      if (match) return match;
    }

    return null;
  }, [courseIndex, data.scanResult, data.scanResults, data.scanIds]);

  // Get the syllabus document ID for this course
  const syllabusDocIdForCourse = React.useMemo(() => {
    if (courseIndex === 0 && data.syllabusDocumentId) return data.syllabusDocumentId;
    if (Array.isArray(data.syllabusDocumentIds) && data.syllabusDocumentIds[courseIndex]) {
      return data.syllabusDocumentIds[courseIndex];
    }
    return null;
  }, [courseIndex, data.syllabusDocumentId, data.syllabusDocumentIds]);

  // Normalize the scan result into courseData AND populate DataContext draft on first mount
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    // In hybrid mode, some courses have scan results and others are manual.
    // Determine whether THIS course index is a manual entry by checking
    // if it's beyond the syllabi range.
    const numSyllabi = (data.scanIds || []).length;
    const isManualCourse = data.manualMode && (!scanResultForCourse || courseIndex >= numSyllabi);

    if (scanResultForCourse && !isManualCourse) {
      try {
        const normalized = normalizeSyllabusResponse(scanResultForCourse);
        setCourseData({
          courseName: normalized.course.courseName || '',
          courseCode: normalized.course.courseCode || '',
          section: normalized.course.section || '',
          semester: normalized.course.semester || '',
          instructor: normalized.course.instructorPrimary || '',
          location: normalized.course.location || '',
          startDate: normalized.course.startDate || '',
          endDate: normalized.course.endDate || '',
          meetingDays: normalized.course.meetingDays || '',
          meetingStartTime: normalized.course.meetingStartTime || '',
          meetingEndTime: normalized.course.meetingEndTime || '',
          description: '',
          syllabusPreview: normalized,
          sessions: normalized.sessions || [],
          assignments: normalized.assignments || [],
          qualityPercentage: (typeof normalized.course.confidence === 'number' ? normalized.course.confidence : null),
        });

        // ── Populate DataContext draft so CourseReviewSection can display them ──
        // Build session events (mirrors CreateCourseWizard.processSyllabus)
        const sessionEvents = (normalized.sessions || []).map(s => ({
          sourceId: s.id || s.eventId || s.event_id || null,
          title: s.title || 'Class Session',
          description: s.description || '',
          date: s.date,
          startTime: s.startTime || normalized.course.meetingStartTime || '00:00',
          endTime: s.endTime || normalized.course.meetingEndTime || '00:00',
          location: normalized.course.location || '',
          eventType: s.canceled ? 'holiday' : 'class_session',
          isRecurring: false,
          sequence: s.sequence,
          isFromSyllabus: true,
          topics: s.topics || [],
          readings: s.readings || [],
          canceled: Boolean(s.canceled),
        }));

        // Map assignments
        const mappedAssignments = (normalized.assignments || []).map(a => ({
          sourceId: a.id || a.assignmentId || a.assignment_id || null,
          title: a.title,
          description: a.description || '',
          assignmentType: a.assignmentType || 'assignment',
          dueDatetime: a.dueDatetime ? toNaiveDateTime(a.dueDatetime) : null,
          points: a.points ?? null,
          priority: (a.weightPercent && a.weightPercent >= 15) ? 'high' : 'medium',
          weightPercent: a.weightPercent ?? null,
          groupWork: Boolean(a.groupWork),
          dueInstructions: a.dueInstructions || '',
          isFromSyllabus: true,
          sessionSequence: a.sessionSequence ?? null,
          eventId: null,
        }));

        // Reading assignments from sessions
        const readingAssignments = (normalized.sessions || []).flatMap(s => {
          if (!s.readings || !s.readings.length) return [];
          return s.readings.map((r, idx) => ({
            sourceId: `reading-${s.sequence || 'x'}-${idx}`,
            title: typeof r === 'string' ? r.slice(0, 140) : (r.title || 'Reading'),
            description: typeof r === 'string' ? r : (r.description || r.title || ''),
            assignmentType: 'reading',
            dueDatetime: s.date ? toNaiveDateTime(`${s.date}T08:00:00`) : null,
            points: null,
            priority: 'LOW',
            weightPercent: null,
            groupWork: false,
            dueInstructions: '',
            isFromSyllabus: true,
            sessionSequence: s.sequence != null ? s.sequence : null,
            eventId: null,
          }));
        });

        // Write into DataContext createCourseDraft
        setCreateCourseDraft(prev => ({
          ...prev,
          draftEvents: normalizeEvents(sessionEvents),
          draftAssignments: [
            ...normalizeAssignments(mappedAssignments),
            ...normalizeAssignments(readingAssignments),
          ],
        }));
      } catch (err) {
        console.error('[CourseReviewStep] Failed to normalize scan result:', err);
        setCourseData(getEmptyCourseData());
      }
    } else {
      // Manual mode or no scan result — use pre-filled data from manual form if available.
      // In hybrid mode, manual entries are offset by the number of syllabi courses.
      const entries = data.manualCourseEntries || [];
      const manualIndex = numSyllabi > 0 ? courseIndex - numSyllabi : courseIndex;
      const manual = entries[manualIndex >= 0 ? manualIndex : courseIndex] || data.manualCourseData || {};
      setCourseData({
        ...getEmptyCourseData(),
        courseName: manual.courseName || '',
        courseCode: manual.courseCode || '',
        instructor: manual.instructor || '',
        location: manual.location || '',
        startDate: manual.startDate || '',
        endDate: manual.endDate || '',
        meetingDays: manual.meetingDays || '',
        meetingStartTime: manual.meetingStartTime || '',
        meetingEndTime: manual.meetingEndTime || '',
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanResultForCourse, data.manualMode, data.manualCourseData, data.manualCourseEntries, courseIndex, setCreateCourseDraft]);

  // Clear the draft when the step unmounts so it doesn't leak into CreateCourseWizard.
  // Also reset initializedRef so React 18 StrictMode's simulated unmount→remount
  // cycle re-runs the init effect and repopulates the draft correctly.
  useEffect(() => {
    return () => {
      initializedRef.current = false;
      setCreateCourseDraft({ draftEvents: [], draftAssignments: [], removedSyllabusSourceIds: [] });
    };
  }, [setCreateCourseDraft]);

  const canCreate = courseData?.courseName?.trim() && courseData?.startDate && courseData?.endDate;

  // --- Course creation handler (mirrors CreateCourseWizard.createCourse) ---
  const createCourse = useCallback(async () => {
    if (!canCreate || loading) return;
    setLoading(true);

    try {
      const userId = user?.userId || user?.id;

      // 1. Resolve endeavor type ("course")
      const typesResp = await EndeavorTypeApi.getEndeavorTypes();
      if (!typesResp.ok) {
        throw new Error(await typesResp.text().catch(() => 'Failed to load endeavor types'));
      }
      const types = await typesResp.json();
      const courseType = (types || []).find(t =>
        t.name?.toLowerCase() === 'course' || t.typeName?.toLowerCase() === 'course'
      );
      const endeavorTypeId = courseType?.endeavorTypeId || courseType?.id;

      // 2. Create endeavor
      const createPayload = {
        name: courseData.courseName,
        description: courseData.description || '',
        startDate: courseData.startDate,
        endDate: courseData.endDate,
        endeavorTypeId,
        userId,
        location: courseData.location || '',
      };
      const createResp = makeShared
        ? await EndeavorApi.createSharedEndeavor(createPayload)
        : await EndeavorApi.createEndeavor(createPayload);
      if (!createResp.ok) {
        throw new Error(await createResp.text().catch(() => 'Failed to create endeavor'));
      }
      const newEndeavor = await createResp.json();
      const courseId = newEndeavor?.endeavorId || newEndeavor?.id;

      // 3. Associate syllabus document for this particular course
      if (syllabusDocIdForCourse) {
        try {
          await DocumentEndeavorApi.associateDocumentWithEndeavor(syllabusDocIdForCourse, courseId);
        } catch (e) { console.warn('[CourseReviewStep] Failed to associate syllabus:', e); }
      }

      // 4. Associate any docs uploaded during scan wait
      if (data.uploadedDocIds?.length > 0) {
        for (const docId of data.uploadedDocIds) {
          try {
            await DocumentEndeavorApi.associateDocumentWithEndeavor(docId, courseId);
          } catch (e) { console.warn('[CourseReviewStep] Failed to associate doc:', docId, e); }
        }
      }

      // 5. Bulk-create all events and assignments in a single request
      //    (mirrors CreateCourseWizard logic so event↔assignment linkage works)
      const draftEvents = Array.isArray(createCourseDraft?.draftEvents) ? createCourseDraft.draftEvents : [];
      const draftAssignments = Array.isArray(createCourseDraft?.draftAssignments) ? createCourseDraft.draftAssignments : [];

      // Helpers (same as CreateCourseWizard)
      const toIso = (date, time) => {
        if (!date) return null;
        const d = String(date).substring(0, 10); // extract YYYY-MM-DD
        // Pad time: '7:00' → '07:00', '9:30' → '09:30'
        let t = time || '';
        if (/^\d:\d{2}$/.test(t)) t = '0' + t;           // 7:00 → 07:00
        const hhmm = (t && t.length === 5) ? `${t}:00` : (t || '00:00:00');
        return sanitizeDateTime(`${d}T${hhmm}`);
      };
      const coerceNaive = (dt) => {
        if (!dt) return dt;
        return String(dt).replace(/([+-]\d{2}:?\d{2}|Z)$/, '');
      };
      const normalizeDue = (val, fallbackDate) => {
        if (!val) {
          if (!fallbackDate) return null;
          return toNaiveDateTime(`${fallbackDate}T23:59:59`);
        }
        if (typeof val === 'string') {
          let v = val;
          if (/^\d{4}-\d{2}-\d{2}$/.test(v)) v = `${v}T23:59:59`;
          return toNaiveDateTime(v);
        }
        return val;
      };

      // Resolve event type for all events
      const eventTypesResp = await EventTypeApi.getEventTypes();
      let eventTypes = [];
      if (eventTypesResp.ok) {
        eventTypes = await eventTypesResp.json();
      }
      const eventTypeNameToId = new Map();
      (eventTypes || []).forEach(t => {
        const name = (t.name || t.typeName || t.eventTypeName || '').toLowerCase();
        const id = t.eventTypeId || t.id;
        if (name && id) eventTypeNameToId.set(name, id);
      });
      const defaultEventTypeId = eventTypeNameToId.get('class session')
        || eventTypeNameToId.get('class')
        || (eventTypeNameToId.size > 0 ? eventTypeNameToId.values().next().value : null);

      // Build bulk event items & sequence→index map for assignment linkage
      const sequenceToIndex = new Map();
      const bulkEvents = draftEvents.map((ev, idx) => {
        if (ev.sequence != null) sequenceToIndex.set(Number(ev.sequence), idx);

        let startIso = null;
        let endIso = null;
        const startRaw = ev.startDatetime || ev.start || null;
        const endRaw = ev.endDatetime || ev.end || null;
        if (startRaw && String(startRaw).includes('T')) startIso = coerceNaive(sanitizeDateTime(startRaw));
        else startIso = coerceNaive(toIso(ev.date || startRaw, ev.startTime || startRaw));
        if (endRaw && String(endRaw).includes('T')) endIso = coerceNaive(sanitizeDateTime(endRaw));
        else endIso = coerceNaive(toIso(ev.date || endRaw, ev.endTime || endRaw || ev.startTime));

        const eventTypeName = (ev.eventType || 'class_session').toLowerCase();
        return {
          name: ev.title || ev.name || `Session ${ev.sequence || idx + 1}`,
          description: ev.description || '',
          startDatetime: startIso,
          endDatetime: endIso,
          eventTypeId: ev.eventTypeId || eventTypeNameToId.get(eventTypeName) || defaultEventTypeId,
          location: ev.location || null,
          sourceId: ev.sourceId || null,
        };
      });

      // Group assignments by sessionSequence for event linkage
      const assignmentsBySeq = (draftAssignments || []).reduce((acc, a) => {
        const seq = a.sessionSequence != null ? Number(a.sessionSequence) : null;
        if (seq != null) {
          if (!acc[seq]) acc[seq] = [];
          acc[seq].push(a);
        }
        return acc;
      }, {});

      // Build bulk assignments — session-linked assignments get tempEventIndex,
      // standalone assignments get null. (Same logic as CreateCourseWizard)
      const bulkAssignments = [];

      // Session-linked assignments
      draftEvents.forEach((ev, evIdx) => {
        const seq = ev.sequence != null ? Number(ev.sequence) : null;
        const sessionAssignments = seq != null ? (assignmentsBySeq[seq] || []) : [];
        for (const a of sessionAssignments) {
          const fallbackDate = ev.startDatetime ? String(ev.startDatetime).slice(0, 10)
            : (ev.date ? String(ev.date).slice(0, 10) : (courseData?.endDate || new Date().toISOString().slice(0, 10)));
          bulkAssignments.push({
            title: a.title || 'Untitled',
            description: a.description || '',
            dueDatetime: normalizeDue(a.dueDatetime || a.dueDate, fallbackDate),
            assignmentType: a.assignmentType || 'assignment',
            priority: typeof a.priority === 'number' ? a.priority : null,
            weightPercent: a.weightPercent ?? null,
            groupWork: Boolean(a.groupWork),
            sourceId: a.sourceId || a.assignmentId || a.id || null,
            tempEventIndex: evIdx,
          });
        }
      });

      // Standalone assignments (no sessionSequence)
      const standaloneAssignments = (draftAssignments || []).filter(a => a.sessionSequence == null);
      for (const a of standaloneAssignments) {
        const fallbackDate = courseData?.endDate || new Date().toISOString().slice(0, 10);
        bulkAssignments.push({
          title: a.title || 'Untitled',
          description: a.description || '',
          dueDatetime: normalizeDue(a.dueDatetime || a.dueDate, fallbackDate),
          assignmentType: a.assignmentType || 'assignment',
          priority: typeof a.priority === 'number' ? a.priority : null,
          weightPercent: a.weightPercent ?? null,
          groupWork: Boolean(a.groupWork),
          sourceId: a.sourceId || a.assignmentId || a.id || null,
          tempEventIndex: null,
        });
      }

      let sessionCount = 0;
      let assignmentCount = 0;

      if (bulkEvents.length > 0 || bulkAssignments.length > 0) {
        const bulkResp = await EndeavorApi.bulkCreateCourseContent(courseId, {
          events: bulkEvents,
          assignments: bulkAssignments,
        });
        if (bulkResp.ok) {
          const bulkResult = await bulkResp.json();
          sessionCount = bulkResult.eventsCreated || 0;
          assignmentCount = bulkResult.assignmentsCreated || 0;
        } else {
          console.warn('[CourseReviewStep] Bulk content create failed:', await bulkResp.text().catch(() => ''));
        }
      }

      // 6. Invalidate caches and hydrate
      try {
        if (clearBulkCache) {
          clearBulkCache(['endeavors_user', 'user-events', 'user-assignments', 'dashboard_full_hydration']);
        } else if (invalidateCache) {
          invalidateCache('endeavors_user');
        }
        const userId = user?.userId || user?.id;
        if (hydrateDashboard && userId) await hydrateDashboard(userId);
      } catch (e) { /* best-effort */ }

      // Mark that the user now has courses so routing defaults to dashboard
      try { patchPreferences({ has_courses: true }); } catch (_) {}

      // Update wizard state — append this course to courses[]
      const newCourse = {
        courseId,
        courseName: courseData.courseName,
        sessionCount,
        assignmentCount,
        uploadedDocIds: [],
        syllabusDocumentId: syllabusDocIdForCourse,
      };
      const updatedCourses = [...(data.courses || [])];
      // Replace if same index already exists (user went back and re-created)
      updatedCourses[courseIndex] = newCourse;

      updateData({
        courses: updatedCourses,
        // Keep legacy singular fields pointing at the first course for backward compat
        courseId: updatedCourses[0]?.courseId || courseId,
        courseName: updatedCourses[0]?.courseName || courseData.courseName,
        sessionCount: updatedCourses[0]?.sessionCount || sessionCount,
        assignmentCount: updatedCourses[0]?.assignmentCount || assignmentCount,
        // Default AI tools to the first course
        aiToolsCourseId: updatedCourses[0]?.courseId || courseId,
      });

      UsageEventsTracker.recordUserAction('ONBOARDING_COURSE_CREATED', {
        action: 'create',
        category: 'onboarding',
        entityType: 'course',
        entityId: courseId,
        metadata: { sessionCount, assignmentCount, hadSyllabus: !!syllabusDocIdForCourse, courseIndex },
      });

      goNext();
    } catch (err) {
      console.error('[CourseReviewStep] Course creation failed:', err);
      UsageEventsTracker.recordUserAction('ONBOARDING_INTERACTION', {
        action: 'creation_error',
        category: 'onboarding',
        label: 'course-review',
        metadata: { courseIndex, errorMessage: err.message || 'Course creation failed' },
      });
      alert('Failed to create course. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [canCreate, loading, user, courseData, data, updateData, goNext, hydrateDashboard, invalidateCache, clearBulkCache, createCourseDraft, courseIndex, syllabusDocIdForCourse, patchPreferences, makeShared]);

  // Skip tracking during initial hydration — only track after first render cycle
  const hydratedRef = useRef(false);
  useEffect(() => { hydratedRef.current = true; }, []);

  const trackedSetCourseData = useCallback((updater) => {
    setCourseData(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      // Track field edits — only after hydration to avoid false positives
      if (hydratedRef.current && prev && next) {
        const editedFields = Object.keys(next).filter(k =>
          k !== 'syllabusPreview' && k !== 'sessions' && k !== 'assignments' &&
          k !== 'qualityPercentage' && next[k] !== prev[k]
        );
        if (editedFields.length > 0) {
          UsageEventsTracker.recordUserAction('ONBOARDING_INTERACTION', {
            action: 'field_edited',
            category: 'onboarding',
            label: 'course-review',
            metadata: {
              fields: editedFields,
              courseIndex,
              wasAutoFilled: !data.manualMode,
            },
          });
        }
      }
      return next;
    });
  }, [courseIndex, data.manualMode]);

  // ── Editing handlers ──────────────────────────────────────
  const startEditSession = useCallback((session) => {
    setEditingSessionId(session.id);
    setEditForm({
      title: session.title || session.name || '',
      date: session.date || (session.start || session.startDatetime || '').slice(0, 10),
      startTime: session.startTime || (session.start || session.startDatetime || '').slice(11, 16) || '',
      endTime: session.endTime || (session.end || session.endDatetime || '').slice(11, 16) || '',
      location: session.location || '',
    });
  }, []);

  const saveEditSession = useCallback(() => {
    if (!editingSessionId) return;
    const patch = { title: editForm.title, location: editForm.location };
    if (editForm.date) {
      patch.date = editForm.date;
      if (editForm.startTime) patch.start = `${editForm.date}T${editForm.startTime}`;
      if (editForm.startTime) patch.startDatetime = `${editForm.date}T${editForm.startTime}`;
      if (editForm.endTime) patch.end = `${editForm.date}T${editForm.endTime}`;
      if (editForm.endTime) patch.endDatetime = `${editForm.date}T${editForm.endTime}`;
    }
    updateDraftEvent(editingSessionId, patch);
    setEditingSessionId(null);
    setEditForm({});
  }, [editingSessionId, editForm, updateDraftEvent]);

  const cancelEditSession = useCallback(() => {
    setEditingSessionId(null);
    setEditForm({});
  }, []);

  const handleDeleteSession = useCallback((id) => {
    deleteDraftEvent(id);
  }, [deleteDraftEvent]);

  const startEditAssignment = useCallback((asn) => {
    setEditingAssignmentId(asn.id);
    const dueRaw = asn.dueDatetime || asn.dueDate || '';
    setEditForm({
      title: asn.title || '',
      dueDate: (typeof dueRaw === 'string' ? dueRaw.slice(0, 10) : ''),
      dueTime: (typeof dueRaw === 'string' ? dueRaw.slice(11, 16) : ''),
      assignmentType: asn.assignmentType || 'assignment',
    });
  }, []);

  const saveEditAssignment = useCallback(() => {
    if (!editingAssignmentId) return;
    const patch = { title: editForm.title, assignmentType: editForm.assignmentType };
    if (editForm.dueDate) {
      const timeStr = editForm.dueTime || '23:59';
      patch.dueDatetime = `${editForm.dueDate}T${timeStr}:00`;
      patch.dueDate = editForm.dueDate;
    }
    updateDraftAssignment(editingAssignmentId, patch);
    setEditingAssignmentId(null);
    setEditForm({});
  }, [editingAssignmentId, editForm, updateDraftAssignment]);

  const cancelEditAssignment = useCallback(() => {
    setEditingAssignmentId(null);
    setEditForm({});
  }, []);

  const handleDeleteAssignment = useCallback((id) => {
    deleteDraftAssignment(id);
  }, [deleteDraftAssignment]);

  const startAddAssignment = useCallback((sessionId) => {
    setAddingAssignmentForSession(sessionId);
    if (sessionId === 'global') {
      // Global add — no prefill date, system will auto-attribute
      setNewAssignmentForm({ title: '', dueDate: '', dueTime: '23:59', assignmentType: 'homework' });
    } else {
      const ev = (createCourseDraft?.draftEvents || []).find(e => e.id === sessionId);
      const sessDate = ev ? (ev.date || (ev.start || ev.startDatetime || '').slice(0, 10)) : '';
      setNewAssignmentForm({ title: '', dueDate: sessDate, dueTime: '23:59', assignmentType: 'homework' });
    }
  }, [createCourseDraft?.draftEvents]);

  const saveNewAssignment = useCallback(() => {
    if (!newAssignmentForm.title.trim()) return;
    const dtStr = newAssignmentForm.dueDate
      ? `${newAssignmentForm.dueDate}T${newAssignmentForm.dueTime || '23:59'}:00`
      : null;
    const tempId = `new-${Date.now()}`;
    const payload = {
      id: tempId,
      title: newAssignmentForm.title,
      assignmentType: newAssignmentForm.assignmentType || 'homework',
      dueDatetime: dtStr ? toNaiveDateTime(dtStr) : null,
      dueDate: newAssignmentForm.dueDate || null,
      description: '',
      priority: 'medium',
      isFromSyllabus: false,
      sessionSequence: null,
    };
    addDraftAssignment(payload);
    setJustAddedId(tempId);
    setTimeout(() => setJustAddedId(null), 1200);
    setAddingAssignmentForSession(null);
    setNewAssignmentForm({ title: '', dueDate: '', dueTime: '23:59', assignmentType: 'homework' });
  }, [newAssignmentForm, addDraftAssignment]);

  const cancelAddAssignment = useCallback(() => {
    setAddingAssignmentForSession(null);
    setNewAssignmentForm({ title: '', dueDate: '', dueTime: '23:59', assignmentType: 'homework' });
  }, []);

  // ── Add Event handlers ──
  const startAddEvent = useCallback((mode) => {
    // mode = 'global' or a sessionId string
    setAddingEventMode(mode);
    let prefillDate = '';
    let prefillStart = '';
    let prefillEnd = '';
    let prefillLocation = '';
    if (mode !== 'global') {
      // Per-session: prefill from that session
      const ev = (createCourseDraft?.draftEvents || []).find(e => e.id === mode);
      if (ev) {
        prefillDate = ev.date || (ev.start || ev.startDatetime || '').slice(0, 10);
        prefillStart = (ev.startTime || (ev.start || ev.startDatetime || '').slice(11, 16)) || '';
        prefillEnd = (ev.endTime || (ev.end || ev.endDatetime || '').slice(11, 16)) || '';
        prefillLocation = ev.location || '';
      }
    }
    setNewEventForm({ title: '', date: prefillDate, startTime: prefillStart, endTime: prefillEnd, location: prefillLocation, eventType: 'class' });
  }, [createCourseDraft?.draftEvents]);

  const saveNewEvent = useCallback(() => {
    if (!newEventForm.title.trim()) return;
    const tempId = `evt-${Date.now()}`;
    const payload = {
      id: tempId,
      title: newEventForm.title,
      date: newEventForm.date || null,
      startTime: newEventForm.startTime || null,
      endTime: newEventForm.endTime || null,
      location: newEventForm.location || '',
      eventType: newEventForm.eventType || 'class',
      source: 'manual',
      isFromSyllabus: false,
    };
    // Let addDraftEvent handle startDatetime/endDatetime normalization
    if (newEventForm.date && newEventForm.startTime) {
      payload.start = `${newEventForm.date}T${newEventForm.startTime}`;
      payload.startDatetime = `${newEventForm.date}T${newEventForm.startTime}:00`;
    }
    if (newEventForm.date && newEventForm.endTime) {
      payload.end = `${newEventForm.date}T${newEventForm.endTime}`;
      payload.endDatetime = `${newEventForm.date}T${newEventForm.endTime}:00`;
    }
    addDraftEvent(payload);
    setJustAddedEventId(tempId);
    setTimeout(() => setJustAddedEventId(null), 1200);
    setAddingEventMode(null);
    setNewEventForm({ title: '', date: '', startTime: '', endTime: '', location: '', eventType: 'class' });
  }, [newEventForm, addDraftEvent]);

  const cancelAddEvent = useCallback(() => {
    setAddingEventMode(null);
    setNewEventForm({ title: '', date: '', startTime: '', endTime: '', location: '', eventType: 'class' });
  }, []);

  // ── Quick schedule generator ──
  const DAY_OPTIONS = [
    { key: 'Mon', label: 'Mon', dow: 1 },
    { key: 'Tue', label: 'Tue', dow: 2 },
    { key: 'Wed', label: 'Wed', dow: 3 },
    { key: 'Thu', label: 'Thu', dow: 4 },
    { key: 'Fri', label: 'Fri', dow: 5 },
    { key: 'Sat', label: 'Sat', dow: 6 },
    { key: 'Sun', label: 'Sun', dow: 0 },
  ];

  const toggleSchedDay = useCallback((key) => {
    setSchedDays(prev => prev.includes(key) ? prev.filter(d => d !== key) : [...prev, key]);
  }, []);

  const generateSchedule = useCallback(() => {
    if (schedDays.length === 0 || !courseData?.startDate || !courseData?.endDate) return;
    const dowMap = Object.fromEntries(DAY_OPTIONS.map(d => [d.key, d.dow]));
    const selectedDows = new Set(schedDays.map(k => dowMap[k]));
    const start = new Date(courseData.startDate + 'T00:00:00');
    const end = new Date(courseData.endDate + 'T00:00:00');
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return;

    const events = [];
    const cur = new Date(start);
    let seq = 1;
    while (cur <= end) {
      if (selectedDows.has(cur.getDay())) {
        const dateStr = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`;
        const tempId = `gen-${Date.now()}-${seq}`;
        events.push({
          id: tempId,
          title: courseData.courseName ? `${courseData.courseName} — Class` : 'Class Session',
          date: dateStr,
          startTime: schedStart || null,
          endTime: schedEnd || null,
          start: schedStart ? `${dateStr}T${schedStart}` : null,
          startDatetime: schedStart ? `${dateStr}T${schedStart}:00` : null,
          end: schedEnd ? `${dateStr}T${schedEnd}` : null,
          endDatetime: schedEnd ? `${dateStr}T${schedEnd}:00` : null,
          location: schedLocation || '',
          eventType: 'class',
          source: 'schedule_generator',
          isFromSyllabus: false,
          sequence: seq,
        });
        seq++;
      }
      cur.setDate(cur.getDate() + 1);
    }

    // Add all generated events to draft
    events.forEach(evt => addDraftEvent(evt));
    setShowScheduleGen(false);

    UsageEventsTracker.recordUserAction('ONBOARDING_INTERACTION', {
      action: 'generate_schedule',
      category: 'onboarding',
      label: 'course-review',
      metadata: { days: schedDays, sessionCount: events.length },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedDays, schedStart, schedEnd, schedLocation, courseData, addDraftEvent]);

  // ────────────────────────────────────────────────────────
  // Unified timeline: group assignments under nearest session
  // ────────────────────────────────────────────────────────
  const draftEvents = useMemo(() => Array.isArray(createCourseDraft?.draftEvents) ? createCourseDraft.draftEvents : [], [createCourseDraft?.draftEvents]);
  const draftAssignments = useMemo(() => Array.isArray(createCourseDraft?.draftAssignments) ? createCourseDraft.draftAssignments : [], [createCourseDraft?.draftAssignments]);

  const parseLocalDate = (raw) => {
    if (!raw) return null;
    const s = String(raw).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [y, m, d] = s.split('-').map(Number);
      return new Date(y, m - 1, d);
    }
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  };

  const sessionTimeline = useMemo(() => {
    // Sort sessions by date
    const sorted = [...draftEvents].sort((a, b) => {
      const da = new Date(a.start || a.startDatetime || a.date || 0).getTime() || 0;
      const db = new Date(b.start || b.startDatetime || b.date || 0).getTime() || 0;
      return da - db;
    });

    // For each assignment, find the best session using attribution rules
    const assignmentMap = new Map(); // sessionId → [{ assignment, relation }]

    for (const asn of draftAssignments) {
      const dueRaw = asn.dueDate || asn.dueDatetime;
      const dueDate = parseLocalDate(dueRaw);

      if (sorted.length === 0) continue; // no sessions to attach to

      if (!dueDate) {
        // No due date → attach to first session
        const key = sorted[0].id;
        if (!assignmentMap.has(key)) assignmentMap.set(key, []);
        assignmentMap.get(key).push({ assignment: asn, relation: null });
        continue;
      }

      let bestSession = null;
      let relation = null; // 'before' | 'same' | 'after' | null

      // 1. Exact same day match
      for (const session of sorted) {
        const sessDate = parseLocalDate(session.start || session.startDatetime || session.date);
        if (sessDate && sessDate.toDateString() === dueDate.toDateString()) {
          bestSession = session;
          relation = 'same';
          break;
        }
      }

      // 2. Next upcoming session (first session on or after due date)
      if (!bestSession) {
        for (const session of sorted) {
          const sessDate = parseLocalDate(session.start || session.startDatetime || session.date);
          if (sessDate && sessDate >= dueDate) {
            bestSession = session;
            relation = 'before'; // due before this session
            break;
          }
        }
      }

      // 3. Due date is after all sessions → attach to last session
      if (!bestSession) {
        bestSession = sorted[sorted.length - 1];
        relation = 'after'; // due after this session
      }

      const key = bestSession.id;
      if (!assignmentMap.has(key)) assignmentMap.set(key, []);
      assignmentMap.get(key).push({ assignment: asn, relation });
    }

    return { sessions: sorted, assignmentMap };
  }, [draftEvents, draftAssignments]);

  // Timeline data — every active day between first and last event/assignment
  const timelineDays = useMemo(() => {
    const dayMap = {};
    for (const e of draftEvents) {
      const d = parseLocalDate(e.start || e.startDatetime || e.date);
      if (!d) continue;
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      if (!dayMap[key]) dayMap[key] = { date: d, sessions: 0, assignments: 0 };
      dayMap[key].sessions++;
    }
    for (const a of draftAssignments) {
      const d = parseLocalDate(a.dueDate || a.dueDatetime);
      if (!d) continue;
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      if (!dayMap[key]) dayMap[key] = { date: d, sessions: 0, assignments: 0 };
      dayMap[key].assignments++;
    }
    return Object.entries(dayMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, val]) => ({
        key,
        ...val,
        weekday: val.date.toLocaleDateString(undefined, { weekday: 'short' }),
        dayNum: val.date.getDate(),
        month: val.date.toLocaleDateString(undefined, { month: 'short' }),
      }));
  }, [draftEvents, draftAssignments]);

  // Format helpers for render
  const fmtSessionDate = (raw) => {
    const d = parseLocalDate(raw);
    if (!d) return '';
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  };
  const fmtTime = (raw) => {
    if (!raw) return '';
    const s = String(raw).trim();
    // Extract HH:MM from ISO or plain time
    const match = s.match(/T?(\d{1,2}:\d{2})/);
    if (!match) return '';
    const [h, m] = match[1].split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  };
  const fmtDateRange = (start, end) => {
    const s = parseLocalDate(start);
    const e = parseLocalDate(end);
    const fmt = (d) => d ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';
    if (s && e) return `${fmt(s)} — ${fmt(e)}`;
    if (s) return `Starts ${fmt(s)}`;
    if (e) return `Ends ${fmt(e)}`;
    return '';
  };

  if (!courseData) {
    return (
      <div className="cr-card" style={{ textAlign: 'center', padding: '64px 24px' }}>
        <div className="onboarding-scan-spinner" style={{ margin: '0 auto 16px' }} />
        <p>Preparing course review...</p>
      </div>
    );
  }

  const { sessions, assignmentMap } = sessionTimeline;

  return (
    <div className="cr-root">
      {/* ── Header ── */}
      <div className="cr-header">
        {totalCourses > 1 && (
          <span className="cr-counter">Course {courseIndex + 1} of {totalCourses}</span>
        )}
        <h2 className="cr-title">{data.manualMode ? 'Build Your Course' : 'Review Your Course'}</h2>
        <p className="cr-subtitle">
          {data.manualMode
            ? 'Fill in your course details below.'
            : 'Scout organized everything from your syllabus. Review the plan below.'}
        </p>
      </div>

      {/* ── Compact Course Summary ── */}
      <div className="cr-summary">
        <div className="cr-summary-name">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/></svg>
          {editingCourseName ? (
            <input
              ref={courseNameInputRef}
              className="cr-summary-name-input"
              type="text"
              value={courseData.courseName || ''}
              placeholder="Name your course"
              autoFocus
              onChange={e => trackedSetCourseData(prev => ({ ...prev, courseName: e.target.value }))}
              onBlur={() => setEditingCourseName(false)}
              onKeyDown={e => { if (e.key === 'Enter') { e.target.blur(); } }}
            />
          ) : (
            <span
              className={`cr-summary-name-text${!courseData.courseName ? ' cr-summary-name-text--empty' : ''}`}
              onClick={() => setEditingCourseName(true)}
            >
              {courseData.courseName || 'Name your course'}
              <svg className="cr-summary-name-pencil" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
            </span>
          )}
        </div>
        <div className="cr-summary-stats">
          {(courseData.startDate || courseData.endDate) && (
            <span className="cr-stat">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4"/><path d="M8 2v4"/><path d="M3 10h18"/></svg>
              {fmtDateRange(courseData.startDate, courseData.endDate)}
            </span>
          )}
          <span className="cr-stat">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
            {draftEvents.length} session{draftEvents.length !== 1 ? 's' : ''}
          </span>
          <span className="cr-stat">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></svg>
            {draftAssignments.length} assignment{draftAssignments.length !== 1 ? 's' : ''}
          </span>
          {courseData.instructor && (
            <span className="cr-stat">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              {courseData.instructor}
            </span>
          )}
        </div>
      </div>

      {/* ── Course Timeline Strip ── */}
      {timelineDays.length > 0 && (
        <div className="cr-timeline-strip">
          <div className="cr-strip-header">
            <span className="cr-strip-label">Course timeline</span>
            <div className="cr-strip-legend">
              <span
                className={`cr-legend-item${legendHover === 'session' ? ' cr-legend-item--active' : ''}`}
                onMouseEnter={() => setLegendHover('session')}
                onMouseLeave={() => setLegendHover(null)}
              >
                <span className="cr-legend-dot cr-legend-dot--session" />
                <span className="cr-legend-text">Event</span>
              </span>
              <span
                className={`cr-legend-item${legendHover === 'assignment' ? ' cr-legend-item--active' : ''}`}
                onMouseEnter={() => setLegendHover('assignment')}
                onMouseLeave={() => setLegendHover(null)}
              >
                <span className="cr-legend-dot cr-legend-dot--assignment" />
                <span className="cr-legend-text">Assignment</span>
              </span>
              <span
                className={`cr-legend-item${legendHover === 'both' ? ' cr-legend-item--active' : ''}`}
                onMouseEnter={() => setLegendHover('both')}
                onMouseLeave={() => setLegendHover(null)}
              >
                <span className="cr-legend-dot cr-legend-dot--both">
                  <span className="cr-legend-half cr-legend-half--left" />
                  <span className="cr-legend-half cr-legend-half--right" />
                </span>
                <span className="cr-legend-text">Both</span>
              </span>
            </div>
          </div>
          <div className="cr-strip-track" data-legend-highlight={legendHover || undefined}>
            {timelineDays.map((day, i) => {
              const hasBoth = day.sessions > 0 && day.assignments > 0;
              const type = hasBoth ? 'both' : day.sessions > 0 ? 'session' : 'assignment';
              const totalAssignments = day.assignments;
              // Show month label on first item or when month changes
              const showMonth = i === 0 || day.month !== timelineDays[i - 1]?.month;

              return (
                <div
                  key={day.key}
                  className={`cr-strip-day cr-strip-day--${type}`}
                  onMouseEnter={() => setHoveredStripDay(day.key)}
                  onMouseLeave={() => setHoveredStripDay(null)}
                >
                  {/* Marker node */}
                  <div className="cr-strip-marker">
                    {hasBoth ? (
                      <span className="cr-strip-node cr-strip-node--both">
                        <span className="cr-strip-node-half cr-strip-node-half--left" />
                        <span className="cr-strip-node-half cr-strip-node-half--right" />
                        {totalAssignments > 1 && (
                          <span className="cr-strip-count">{totalAssignments}</span>
                        )}
                      </span>
                    ) : type === 'session' ? (
                      <span className="cr-strip-node cr-strip-node--session" />
                    ) : (
                      <span className="cr-strip-node cr-strip-node--assignment">
                        {totalAssignments > 1 && (
                          <span className="cr-strip-count">{totalAssignments}</span>
                        )}
                      </span>
                    )}
                  </div>
                  {/* Day label */}
                  <span className="cr-strip-weekday">{day.weekday}</span>
                  <span className="cr-strip-daynum">
                    {showMonth && <span className="cr-strip-month">{day.month} </span>}
                    {day.dayNum}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Global timeline actions ── */}
      <div className="cr-timeline-actions">
        {addingEventMode === 'global' ? (
          <div className="cr-add-panel cr-add-panel--event cr-add-panel--global">
            <div className="cr-add-panel-header cr-add-panel-header--event">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              <span>New event</span>
            </div>
            <input className="cr-add-input cr-add-input--name cr-add-input--event" type="text" value={newEventForm.title} onChange={e => setNewEventForm(f => ({ ...f, title: e.target.value }))} placeholder="Event title (e.g. Lecture, Lab, Office Hours)" autoFocus />
            <div className="cr-add-row">
              <div className="cr-add-field"><label className="cr-add-label">Date</label><input className="cr-add-input" type="date" value={newEventForm.date} onChange={e => setNewEventForm(f => ({ ...f, date: e.target.value }))} /></div>
              <div className="cr-add-field"><label className="cr-add-label">Start</label><input className="cr-add-input" type="time" value={newEventForm.startTime} onChange={e => setNewEventForm(f => ({ ...f, startTime: e.target.value }))} /></div>
              <div className="cr-add-field"><label className="cr-add-label">End</label><input className="cr-add-input" type="time" value={newEventForm.endTime} onChange={e => setNewEventForm(f => ({ ...f, endTime: e.target.value }))} /></div>
            </div>
            <div className="cr-add-field"><label className="cr-add-label">Location</label><input className="cr-add-input" type="text" value={newEventForm.location} onChange={e => setNewEventForm(f => ({ ...f, location: e.target.value }))} placeholder="Room or building (optional)" /></div>
            <div className="cr-add-field">
              <label className="cr-add-label">Type</label>
              <div className="cr-add-chips">
                {[
                  { value: 'class', label: 'Class', icon: '📚' },
                  { value: 'exam', label: 'Exam', icon: '📝' },
                  { value: 'lab', label: 'Lab', icon: '🧪' },
                  { value: 'office_hours', label: 'Office Hours', icon: '🕐' },
                  { value: 'review_session', label: 'Review', icon: '🔄' },
                  { value: 'guest_speaker', label: 'Guest Speaker', icon: '🎤' },
                  { value: 'other', label: 'Other', icon: '📌' },
                ].map(t => (
                  <button key={t.value} type="button" className={`cr-add-chip cr-add-chip--event${newEventForm.eventType === t.value ? ' cr-add-chip--active cr-add-chip--event-active' : ''}`} onClick={() => setNewEventForm(f => ({ ...f, eventType: t.value }))}>
                    <span className="cr-add-chip-icon">{t.icon}</span>{t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="cr-add-actions">
              <button className="cr-add-cancel" onClick={cancelAddEvent}>Cancel</button>
              <button className="cr-add-submit cr-add-submit--event" onClick={saveNewEvent} disabled={!newEventForm.title.trim()}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14"/><path d="M5 12h14"/></svg>
                Add Event
              </button>
            </div>
          </div>
        ) : addingAssignmentForSession === 'global' ? (
          <div className="cr-add-panel cr-add-panel--global">
            <div className="cr-add-panel-header">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14"/><path d="M5 12h14"/></svg>
              <span>New assignment</span>
              <span className="cr-add-context">Auto-assigned to nearest session</span>
            </div>
            <input className="cr-add-input cr-add-input--name" type="text" value={newAssignmentForm.title} onChange={e => setNewAssignmentForm(f => ({ ...f, title: e.target.value }))} placeholder="Assignment name" autoFocus />
            <div className="cr-add-row">
              <div className="cr-add-field"><label className="cr-add-label">Due date</label><input className="cr-add-input" type="date" value={newAssignmentForm.dueDate} onChange={e => setNewAssignmentForm(f => ({ ...f, dueDate: e.target.value }))} /></div>
              <div className="cr-add-field"><label className="cr-add-label">Time</label><input className="cr-add-input" type="time" value={newAssignmentForm.dueTime} onChange={e => setNewAssignmentForm(f => ({ ...f, dueTime: e.target.value }))} /></div>
            </div>
            <div className="cr-add-field">
              <label className="cr-add-label">Type</label>
              <div className="cr-add-chips">
                {[
                  { value: 'homework', label: 'Homework', icon: '📝' },
                  { value: 'exam', label: 'Exam', icon: '📋' },
                  { value: 'quiz', label: 'Quiz', icon: '❓' },
                  { value: 'project', label: 'Project', icon: '🔬' },
                  { value: 'essay', label: 'Essay', icon: '✍️' },
                  { value: 'lab', label: 'Lab', icon: '🧪' },
                  { value: 'reading', label: 'Reading', icon: '📖' },
                  { value: 'discussion', label: 'Discussion', icon: '💬' },
                  { value: 'presentation', label: 'Presentation', icon: '📊' },
                ].map(t => (
                  <button key={t.value} type="button" className={`cr-add-chip${newAssignmentForm.assignmentType === t.value ? ' cr-add-chip--active' : ''}`} onClick={() => setNewAssignmentForm(f => ({ ...f, assignmentType: t.value }))}>
                    <span className="cr-add-chip-icon">{t.icon}</span>{t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="cr-add-actions">
              <button className="cr-add-cancel" onClick={cancelAddAssignment}>Cancel</button>
              <button className="cr-add-submit" onClick={saveNewAssignment} disabled={!newAssignmentForm.title.trim()}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14"/><path d="M5 12h14"/></svg>
                Add Assignment
              </button>
            </div>
          </div>
        ) : (
          null
        )}
      </div>

      {/* ── Session-based Timeline ── */}
      <div className="cr-timeline">
        {sessions.map((session, idx) => {
          const sessDate = session.start || session.startDatetime || session.date;
          const linkedAssignments = assignmentMap.get(session.id) || [];
          const startTime = fmtTime(session.start || session.startDatetime || session.startTime);
          const endTime = fmtTime(session.end || session.endDatetime || session.endTime);
          const timeStr = startTime && endTime ? `${startTime} – ${endTime}` : startTime || '';
          const isEditingThis = editingSessionId === session.id;
          const sessDateKey = toYMD(sessDate);
          const isStripHovered = hoveredStripDay && sessDateKey === hoveredStripDay;

          return (
            <div key={session.id} className={`cr-session-card${isStripHovered ? ' cr-session-card--highlighted' : ''}${justAddedEventId === session.id ? ' cr-session-card--just-added' : ''}`}>
              {/* Timeline connector */}
              <div className="cr-connector">
                <span className="cr-connector-dot" />
                {idx < sessions.length - 1 && <span className="cr-connector-line" />}
              </div>

              <div className="cr-session-body">
                {isEditingThis ? (
                  /* ── Inline edit mode ── */
                  <div className="cr-edit-form">
                    <div className="cr-edit-row">
                      <input
                        className="cr-edit-input cr-edit-input--title"
                        type="text"
                        value={editForm.title || ''}
                        onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                        placeholder="Session title"
                        autoFocus
                      />
                    </div>
                    <div className="cr-edit-row cr-edit-row--fields">
                      <input
                        className="cr-edit-input"
                        type="date"
                        value={editForm.date || ''}
                        onChange={e => setEditForm(f => ({ ...f, date: e.target.value }))}
                      />
                      <input
                        className="cr-edit-input"
                        type="time"
                        value={editForm.startTime || ''}
                        onChange={e => setEditForm(f => ({ ...f, startTime: e.target.value }))}
                        placeholder="Start"
                      />
                      <span className="cr-edit-dash">–</span>
                      <input
                        className="cr-edit-input"
                        type="time"
                        value={editForm.endTime || ''}
                        onChange={e => setEditForm(f => ({ ...f, endTime: e.target.value }))}
                        placeholder="End"
                      />
                      <input
                        className="cr-edit-input"
                        type="text"
                        value={editForm.location || ''}
                        onChange={e => setEditForm(f => ({ ...f, location: e.target.value }))}
                        placeholder="Location"
                      />
                    </div>
                    <div className="cr-edit-actions">
                      <button className="cr-edit-btn cr-edit-btn--save" onClick={saveEditSession}>Save</button>
                      <button className="cr-edit-btn cr-edit-btn--cancel" onClick={cancelEditSession}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  /* ── Read mode ── */
                  <>
                    <div className="cr-session-head">
                      <span className="cr-date-badge">{fmtSessionDate(sessDate)}</span>
                      <div className="cr-session-head-row">
                        <h4 className="cr-session-title">{session.title || session.name || `Session ${session.sequence || idx + 1}`}</h4>
                        <div className="cr-item-actions">
                          <button className="cr-action-btn" onClick={() => startEditSession(session)} title="Edit session" aria-label="Edit session">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
                          </button>
                          <button className="cr-action-btn cr-action-btn--danger" onClick={() => handleDeleteSession(session.id)} title="Delete session" aria-label="Delete session">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                          </button>
                        </div>
                      </div>
                      <div className="cr-session-meta">
                        {timeStr && (
                          <span className="cr-meta-pill">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                            {timeStr}
                          </span>
                        )}
                        {(session.location || courseData.location) && (
                          <span className="cr-meta-pill">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
                            {session.location || courseData.location}
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* Linked assignments */}
                {linkedAssignments.length > 0 && (
                  <div className="cr-assignments">
                    <span className="cr-assignments-label">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></svg>
                      {linkedAssignments.length} assignment{linkedAssignments.length !== 1 ? 's' : ''}
                    </span>
                    <ul className="cr-assignment-list">
                      {linkedAssignments.map(({ assignment: a, relation }) => {
                        const isEditingAsn = editingAssignmentId === a.id;
                        if (isEditingAsn) {
                          return (
                            <li key={a.id} className="cr-assignment-item cr-assignment-item--editing">
                              <div className="cr-edit-form cr-edit-form--inline">
                                <input
                                  className="cr-edit-input cr-edit-input--sm"
                                  type="text"
                                  value={editForm.title || ''}
                                  onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                                  placeholder="Assignment name"
                                  autoFocus
                                />
                                <input
                                  className="cr-edit-input cr-edit-input--sm"
                                  type="date"
                                  value={editForm.dueDate || ''}
                                  onChange={e => setEditForm(f => ({ ...f, dueDate: e.target.value }))}
                                />
                                <input
                                  className="cr-edit-input cr-edit-input--sm"
                                  type="time"
                                  value={editForm.dueTime || ''}
                                  onChange={e => setEditForm(f => ({ ...f, dueTime: e.target.value }))}
                                />
                                <select
                                  className="cr-edit-input cr-edit-input--sm"
                                  value={editForm.assignmentType || 'assignment'}
                                  onChange={e => setEditForm(f => ({ ...f, assignmentType: e.target.value }))}
                                >
                                  <option value="assignment">Assignment</option>
                                  <option value="exam">Exam</option>
                                  <option value="quiz">Quiz</option>
                                  <option value="reading">Reading</option>
                                  <option value="project">Project</option>
                                  <option value="lab">Lab</option>
                                </select>
                                <div className="cr-edit-actions cr-edit-actions--inline">
                                  <button className="cr-edit-btn cr-edit-btn--save" onClick={saveEditAssignment}>Save</button>
                                  <button className="cr-edit-btn cr-edit-btn--cancel" onClick={cancelEditAssignment}>Cancel</button>
                                </div>
                              </div>
                            </li>
                          );
                        }
                        const dueRaw = a.dueDate || a.dueDatetime;
                        const dueStr = fmtSessionDate(dueRaw);
                        const dueTime = fmtTime(a.dueDatetime);
                        const relationLabel = relation === 'before' ? 'Due before this session'
                          : relation === 'after' ? 'Due after this session'
                          : relation === 'same' ? null
                          : null;
                        return (
                          <li key={a.id} className={`cr-assignment-item${justAddedId === a.id ? ' cr-assignment-item--just-added' : ''}`}>
                            <span className="cr-assignment-dot" />
                            <div className="cr-assignment-info">
                              <span className="cr-assignment-title">{a.title}</span>
                              <span className="cr-assignment-due">
                                {dueStr ? <>Due {dueStr}{dueTime ? ` \u2022 ${dueTime}` : ''}</> : ''}
                              </span>
                              {relationLabel && <span className="cr-assignment-relation">{relationLabel}</span>}
                            </div>
                            {a.assignmentType && a.assignmentType !== 'assignment' && (
                              <span className="cr-assignment-type">{a.assignmentType}</span>
                            )}
                            <div className="cr-item-actions cr-item-actions--sm">
                              <button className="cr-action-btn cr-action-btn--sm" onClick={() => startEditAssignment(a)} title="Edit" aria-label="Edit assignment">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
                              </button>
                              <button className="cr-action-btn cr-action-btn--sm cr-action-btn--danger" onClick={() => handleDeleteAssignment(a.id)} title="Delete" aria-label="Delete assignment">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {/* Add assignment form */}
                {addingAssignmentForSession === session.id ? (
                  <div className="cr-add-panel" ref={addPanelRef}>
                    <div className="cr-add-panel-header">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14"/><path d="M5 12h14"/></svg>
                      <span>New assignment</span>
                      {sessDate && <span className="cr-add-context">Adding to {fmtSessionDate(sessDate)}</span>}
                    </div>

                    <input
                      className="cr-add-input cr-add-input--name"
                      type="text"
                      value={newAssignmentForm.title}
                      onChange={e => setNewAssignmentForm(f => ({ ...f, title: e.target.value }))}
                      placeholder="Assignment name"
                      autoFocus
                    />

                    <div className="cr-add-row">
                      <div className="cr-add-field">
                        <label className="cr-add-label">Due date</label>
                        <input
                          className="cr-add-input"
                          type="date"
                          value={newAssignmentForm.dueDate}
                          onChange={e => setNewAssignmentForm(f => ({ ...f, dueDate: e.target.value }))}
                        />
                      </div>
                      <div className="cr-add-field">
                        <label className="cr-add-label">Time</label>
                        <input
                          className="cr-add-input"
                          type="time"
                          value={newAssignmentForm.dueTime}
                          onChange={e => setNewAssignmentForm(f => ({ ...f, dueTime: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="cr-add-field">
                      <label className="cr-add-label">Type</label>
                      <div className="cr-add-chips">
                        {[
                          { value: 'homework', label: 'Homework', icon: '📝' },
                          { value: 'exam', label: 'Exam', icon: '📋' },
                          { value: 'quiz', label: 'Quiz', icon: '❓' },
                          { value: 'project', label: 'Project', icon: '🔬' },
                          { value: 'essay', label: 'Essay', icon: '✍️' },
                          { value: 'lab', label: 'Lab', icon: '🧪' },
                          { value: 'reading', label: 'Reading', icon: '📖' },
                          { value: 'discussion', label: 'Discussion', icon: '💬' },
                          { value: 'presentation', label: 'Presentation', icon: '📊' },
                        ].map(t => (
                          <button
                            key={t.value}
                            type="button"
                            className={`cr-add-chip${newAssignmentForm.assignmentType === t.value ? ' cr-add-chip--active' : ''}`}
                            onClick={() => setNewAssignmentForm(f => ({ ...f, assignmentType: t.value }))}
                          >
                            <span className="cr-add-chip-icon">{t.icon}</span>
                            {t.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="cr-add-actions">
                      <button className="cr-add-cancel" onClick={cancelAddAssignment}>Cancel</button>
                      <button className="cr-add-submit" onClick={saveNewAssignment} disabled={!newAssignmentForm.title.trim()}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14"/><path d="M5 12h14"/></svg>
                        Add Assignment
                      </button>
                    </div>
                  </div>
                ) : (
                  <button className="cr-add-btn" onClick={() => startAddAssignment(session.id)}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14"/><path d="M5 12h14"/></svg>
                    Add assignment
                  </button>
                )}

                {/* Add event form (per-session) */}
                {addingEventMode === session.id ? (
                  <div className="cr-add-panel cr-add-panel--event">
                    <div className="cr-add-panel-header cr-add-panel-header--event">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                      <span>New event</span>
                      {sessDate && <span className="cr-add-context">Adding to {fmtSessionDate(sessDate)}</span>}
                    </div>

                    <input
                      className="cr-add-input cr-add-input--name cr-add-input--event"
                      type="text"
                      value={newEventForm.title}
                      onChange={e => setNewEventForm(f => ({ ...f, title: e.target.value }))}
                      placeholder="Event title"
                      autoFocus
                    />

                    <div className="cr-add-row">
                      <div className="cr-add-field">
                        <label className="cr-add-label">Date</label>
                        <input className="cr-add-input" type="date" value={newEventForm.date} onChange={e => setNewEventForm(f => ({ ...f, date: e.target.value }))} />
                      </div>
                      <div className="cr-add-field">
                        <label className="cr-add-label">Start</label>
                        <input className="cr-add-input" type="time" value={newEventForm.startTime} onChange={e => setNewEventForm(f => ({ ...f, startTime: e.target.value }))} />
                      </div>
                      <div className="cr-add-field">
                        <label className="cr-add-label">End</label>
                        <input className="cr-add-input" type="time" value={newEventForm.endTime} onChange={e => setNewEventForm(f => ({ ...f, endTime: e.target.value }))} />
                      </div>
                    </div>

                    <div className="cr-add-field">
                      <label className="cr-add-label">Location</label>
                      <input className="cr-add-input" type="text" value={newEventForm.location} onChange={e => setNewEventForm(f => ({ ...f, location: e.target.value }))} placeholder="Room or building (optional)" />
                    </div>

                    <div className="cr-add-field">
                      <label className="cr-add-label">Type</label>
                      <div className="cr-add-chips">
                        {[
                          { value: 'class', label: 'Class', icon: '📚' },
                          { value: 'exam', label: 'Exam', icon: '📝' },
                          { value: 'lab', label: 'Lab', icon: '🧪' },
                          { value: 'office_hours', label: 'Office Hours', icon: '🕐' },
                          { value: 'review_session', label: 'Review', icon: '🔄' },
                          { value: 'guest_speaker', label: 'Guest Speaker', icon: '🎤' },
                          { value: 'other', label: 'Other', icon: '📌' },
                        ].map(t => (
                          <button
                            key={t.value}
                            type="button"
                            className={`cr-add-chip cr-add-chip--event${newEventForm.eventType === t.value ? ' cr-add-chip--active cr-add-chip--event-active' : ''}`}
                            onClick={() => setNewEventForm(f => ({ ...f, eventType: t.value }))}
                          >
                            <span className="cr-add-chip-icon">{t.icon}</span>
                            {t.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="cr-add-actions">
                      <button className="cr-add-cancel" onClick={cancelAddEvent}>Cancel</button>
                      <button className="cr-add-submit cr-add-submit--event" onClick={saveNewEvent} disabled={!newEventForm.title.trim()}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14"/><path d="M5 12h14"/></svg>
                        Add Event
                      </button>
                    </div>
                  </div>
                ) : (
                  <button className="cr-add-btn cr-add-btn--event" onClick={() => startAddEvent(session.id)}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                    Add event
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {sessions.length === 0 && draftAssignments.length === 0 && (
          <div className="cr-empty cr-empty--builder">
            <p className="cr-builder-hint">
              Nothing was pulled from your syllabus — add your schedule manually.
            </p>

            {/* Two equal primary actions */}
            <div className="cr-builder-actions">
              <button className="cr-builder-btn" onClick={() => startAddEvent('global')}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                + Add Event
              </button>
              <button className="cr-builder-btn" onClick={() => startAddAssignment('global')}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
                + Add Assignment
              </button>
            </div>

            {/* Schedule generator */}
            {courseData?.startDate && courseData?.endDate && (
              <div className="cr-schedgen">
                {!showScheduleGen ? (
                  <button className="cr-schedgen-toggle" onClick={() => setShowScheduleGen(true)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                    Generate recurring schedule
                  </button>
                ) : (
                  <div className="cr-schedgen-form">
                    <span className="cr-schedgen-title">Generate Schedule</span>
                    <div className="cr-schedgen-days">
                      {DAY_OPTIONS.map(d => (
                        <button
                          key={d.key}
                          type="button"
                          className={`cr-schedgen-day${schedDays.includes(d.key) ? ' cr-schedgen-day--active' : ''}`}
                          onClick={() => toggleSchedDay(d.key)}
                        >
                          {d.label}
                        </button>
                      ))}
                    </div>
                    <div className="cr-schedgen-row">
                      <div className="cr-schedgen-field">
                        <label className="cr-schedgen-label">Start</label>
                        <input className="cr-schedgen-input" type="time" value={schedStart} onChange={e => setSchedStart(e.target.value)} />
                      </div>
                      <div className="cr-schedgen-field">
                        <label className="cr-schedgen-label">End</label>
                        <input className="cr-schedgen-input" type="time" value={schedEnd} onChange={e => setSchedEnd(e.target.value)} />
                      </div>
                      <div className="cr-schedgen-field cr-schedgen-field--wide">
                        <label className="cr-schedgen-label">Location</label>
                        <input className="cr-schedgen-input" type="text" value={schedLocation} onChange={e => setSchedLocation(e.target.value)} placeholder="Optional" />
                      </div>
                    </div>
                    <div className="cr-schedgen-actions">
                      <button className="cr-schedgen-cancel" onClick={() => setShowScheduleGen(false)}>Cancel</button>
                      <button className="cr-schedgen-submit" onClick={generateSchedule} disabled={schedDays.length === 0}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                        Generate Schedule
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Empty timeline placeholder */}
            <div className="cr-empty-timeline">
              <div className="cr-empty-timeline-line" />
              <p className="cr-empty-timeline-text">Your timeline will appear here</p>
              <p className="cr-empty-timeline-sub">Add events to generate your schedule</p>
            </div>
          </div>
        )}
      </div>

      {/* ── Shared course match banner ── */}
      <CourseMatchBanner
        courseName={courseData?.courseName}
        instructor={courseData?.instructor}
        onJoined={() => {
          updateData({ joinedSharedCourse: true });
          goNext();
        }}
      />

      {/* ── CTA ── */}
      <div className="cr-cta">
        <p className="cr-cta-summary">
          {draftEvents.length > 0 || draftAssignments.length > 0
            ? <>{draftEvents.length} session{draftEvents.length !== 1 ? 's' : ''} &middot; {draftAssignments.length} assignment{draftAssignments.length !== 1 ? 's' : ''} organized</>
            : 'Add at least one event or assignment to continue'
          }
        </p>

        <label className="cr-shared-toggle">
          <input
            type="checkbox"
            checked={makeShared}
            onChange={e => setMakeShared(e.target.checked)}
          />
          <span className="cr-shared-toggle-label">
            Make this a shared course
            <span className="cr-shared-toggle-hint">Students can join with an invite code</span>
          </span>
        </label>

        <div className="cr-cta-actions">
          <button className="cr-btn cr-btn--back" onClick={goBack}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
            Back
          </button>
          <button
            className={`cr-btn cr-btn--create${draftEvents.length === 0 && draftAssignments.length === 0 ? ' cr-btn--create-muted' : ''}`}
            onClick={createCourse}
            disabled={!canCreate || loading}
          >
            {loading ? (
              <>
                <span className="cr-spinner" />
                Creating…
              </>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></svg>
                Create Course
              </>
            )}
          </button>
        </div>
        <button className="cr-skip-link" onClick={skipCourse} type="button">
          Skip this course for now
        </button>
      </div>
    </div>
  );
}

function getEmptyCourseData() {
  return {
    courseName: '',
    courseCode: '',
    section: '',
    semester: '',
    instructor: '',
    location: '',
    startDate: '',
    endDate: '',
    meetingDays: '',
    meetingStartTime: '',
    meetingEndTime: '',
    description: '',
    syllabusPreview: null,
    sessions: [],
    assignments: [],
    qualityPercentage: null,
  };
}
